import { Icon } from '@aibees/shared/components';
import { PlanInterval } from '@aibees/shared/definition';
import { SubscriptionExpirationStatus } from '@aibees/shared/features/global-notification-bar/components/subscription-bar';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { usePaymentStatus } from '@aibees/shared/hooks/payment/use-payment-status';
import { classnames, fDate, fNumber, toPercent } from '@aibees/shared/utils';
import { Box, Stack, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { CurrentPlanCardProps as Props } from './types';

const getSubscriptionTypeText = (isExpired: any, currentPlan: any) => {
  if (isExpired) {
    return '14-Day Free Trial (Business)';
  }

  if (currentPlan?.interval === 'month') {
    return 'Monthly';
  }

  return 'Yearly';
};

export const CurrentPlanCard = memo(
  ({ className, currentPlan, logoUrl, expireInfo }: Props) => {
    const { status, remainingDuration, isExpired } = expireInfo;
    const { data } = useCurrentSubscriptionApi();
    const { paymentFailed } = usePaymentStatus();

    const isFree = currentPlan?.plan?.metadata?.is_free;

    const lowOnCredit =
      toPercent(
        Number(data?.credit?.total),
        Number(data?.current?.plan?.metadata.request)
      ) <= 5;

    const planDetails = [
      {
        name: 'Plan Type:',
        value: (
          <Typography variant="body4" fontWeight={700} color="white">
            {currentPlan?.plan?.product?.name
              ? currentPlan.plan?.product?.name
              : isFree
              ? 'Free plan'
              : 'Usage Based-Plan'}
          </Typography>
        ),
        visible: true,
      },
      {
        name: 'Credits in Plan:',
        value: (
          <Stack sx={SX.detailBox}>
            <Icon name="coin" size={12} color="primary.500" />
            <Typography variant="body5" fontWeight={400}>
              {fNumber(Number(currentPlan?.plan?.metadata.request))}{' '}
            </Typography>
          </Stack>
        ),
        visible: true,
      },
      {
        name: 'Credits Cost in Plan:',
        value: (
          <Stack sx={SX.detailBox}>
            <Typography variant="body5" fontWeight={400}>
              ${currentPlan?.plan?.amount}
            </Typography>
          </Stack>
        ),
        visible: true,
      },
      {
        name: `Total Seats: ${currentPlan?.plan?.metadata.seat}`,
        value: (
          <Stack sx={SX.detailBox}>
            <Icon name="person-outline" size={12} color="primary.500" />
            <Typography variant="body5" fontWeight={400}>
              {currentPlan?.plan?.metadata.seat} Free
            </Typography>
          </Stack>
        ),
        visible: Boolean(currentPlan?.plan?.metadata.seat),
      },
      {
        name: 'Billing Cycle:',
        value: (
          <Stack sx={SX.detailBox}>
            <Typography variant="body5" fontWeight={400}>
              {currentPlan?.plan?.interval === PlanInterval.MONTH
                ? 'Monthly'
                : 'Yearly'}
            </Typography>
          </Stack>
        ),
        visible: true,
      },
      {
        name: isExpired ? 'Trial Days Left' : 'Next Charge in',
        value: paymentFailed ? (
          <Box sx={SX.paymentFailed}>Pending on Payment</Box>
        ) : (
          <Box sx={SX.remainingDaysWrapper}>
            {status === SubscriptionExpirationStatus.active &&
              !isExpired &&
              fDate(data?.current?.currentPeriodEnd)}
          </Box>
        ),
        visible: !!currentPlan && !isExpired,
        sx: SX.remainingBox(lowOnCredit),
      },
    ];

    return (
      <Styled.Wrapper
        className={classnames('CurrentPlanCard', className, {
          isFree: isFree && !lowOnCredit,
          isLowOnCredit: lowOnCredit,
        })}
        sx={SX.root}
      >
        <Stack gap={1} sx={SX.content}>
          <Stack direction="row" alignItems="center" gap={1}>
            {logoUrl && <Styled.Logo src={logoUrl} />}
            <Stack>
              <Typography variant="body2" fontWeight={700} color="white">
                Plan Summary
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {isExpired
                  ? 'Trial Ended'
                  : currentPlan?.plan?.metadata.name || ''}
              </Typography>
            </Stack>
          </Stack>
          {planDetails.map((i) => (
            <Stack
              key={i.name}
              sx={mergeSx(SX.detailValue(i.visible), i.sx)}
              alignItems="flex-start"
            >
              <Typography variant="body4" fontWeight={400} color="white">
                {i.name}
              </Typography>
              {i.value}
            </Stack>
          ))}
        </Stack>
      </Styled.Wrapper>
    );
  }
);

CurrentPlanCard.displayName = 'CurrentPlanCard';
